/**
 * YEditorBinding — editor.js ↔ Y.Doc 양방향 동기화
 *
 * Local → Remote:
 *   editor.js 내부 eventsDispatcher의 BlockChanged 이벤트를 구독하여
 *   블록 변경을 Y.Doc transaction으로 변환.
 *
 * Remote → Local:
 *   yBlocks.observeDeep()으로 원격 변경을 감지하여
 *   editor.js blocks API(insert/delete/move)와 DOM 직접 patch로 반영.
 *
 * 핵심 주의사항:
 *   - Yjs observeDeep 콜백 안에서 event.changes.delta를 **즉시** 읽어 snapshot으로 변환해야 함.
 *     콜백 종료 후 접근하면 "You must not compute changes after the event-handler fired" 오류.
 *   - 원격 텍스트 변경은 blocks.update() 대신 DOM 직접 patch (blocks.update = DOM 전체 교체 → caret 파괴)
 */
import * as Y from 'yjs'
import { getTextOffset } from './color_utils'

const TEXT_BLOCK_TYPES = new Set(['paragraph', 'header', 'quote'])

// y-rb가 Ruby Integer를 BigInt로 변환하는 경우 대응
function safeClone(obj) {
  return JSON.parse(JSON.stringify(obj, (_, v) => typeof v === 'bigint' ? Number(v) : v))
}

export class YEditorBinding {
  constructor({ editor, doc, yBlocks, awareness, provider, nestedEditorRegistry, awarenessCursor, undoManager }) {
    this.editor = editor
    this.doc = doc
    this.yBlocks = yBlocks
    this.awareness = awareness
    this.provider = provider
    this.nestedEditorRegistry = nestedEditorRegistry || new Map()
    this.awarenessCursor = awarenessCursor || null
    this.undoManager = undoManager || null

    this.isApplyingRemote = false
    this.isApplyingLocal = false
    this.destroyed = false
    this._lastRemoteApplyAt = 0 // 마지막 원격 적용 시각 (무한 루프 방지)

    this._snapshotQueue = []
    this._flushTimer = null

    // Tune/Tool이 이벤트 경로를 우회해 직접 로컬 sync를 트리거할 수 있도록
    // redactor DOM에 binding 참조를 노출한다. (editor.collab의 onBlockChange가
    // 일부 변경 경로에서 발화되지 않는 문제를 안정적으로 우회)
    this._exposeOnRedactor()

    this._setupYjsObserver()
    this._setupEditorObserver()
    this._initialRender()
  }

  _exposeOnRedactor() {
    try {
      const holderEl = document.getElementById(this.editor.configuration?.holder)
      const redactor = holderEl?.querySelector('.codex-editor__redactor')
      if (redactor) redactor._yBinding = this
    } catch { /* noop */ }
  }

  /**
   * 외부(tune/tool)에서 명시적으로 로컬 sync를 예약할 때 호출.
   * _scheduleLocalSync 내부는 isApplyingRemote 가드를 거치므로 루프 염려 없음.
   */
  requestLocalSync() {
    if (this.destroyed) return
    this._scheduleLocalSync()
  }

  destroy() {
    this.destroyed = true
    this.yBlocks.unobserveDeep(this._yjsObserverHandler)
    if (this._editorChangeCleanup) this._editorChangeCleanup()
    if (this._flushTimer) clearTimeout(this._flushTimer)
  }

  // ──────────────────────────────────────
  // Initial Render
  // ──────────────────────────────────────

  async _initialRender() {
    const blocks = this._yBlocksToEditorData()
    if (blocks.length === 0) return

    this.isApplyingRemote = true
    try {
      await this.editor.blocks.render({ blocks })
    } finally {
      this.isApplyingRemote = false
    }
  }

  // ──────────────────────────────────────
  // Yjs → Editor (Remote changes)
  // ──────────────────────────────────────

  _setupYjsObserver() {
    this._yjsObserverHandler = (events, transaction) => {
      // 적용해야 할 origin:
      //   - provider: 원격(서버) 변경
      //   - undoManager: 로컬 Cmd+Z/Cmd+Shift+Z (Y.Doc만 바뀌고 로컬 DOM 미반영되는 버그 방지)
      // 그 외(null = local sync 진행 중 등)는 무시
      const isRemote = transaction.origin === this.provider
      const isUndoRedo = this.undoManager && transaction.origin === this.undoManager
      if (!isRemote && !isUndoRedo) return
      if (this.isApplyingLocal) return
      if (this.destroyed) return

      // 콜백 안에서 즉시 snapshot으로 변환 (콜백 종료 후에는 event.changes 접근 불가)
      const snapshots = []
      for (const event of events) {
        const s = this._snapshotEvent(event)
        if (s) snapshots.push(s)
      }

      if (snapshots.length === 0) return
      this._snapshotQueue.push(...snapshots)

      if (!this._flushTimer) {
        this._flushTimer = setTimeout(() => {
          this._flushTimer = null
          this._flushSnapshotQueue()
        }, 0)
      }
    }
    this.yBlocks.observeDeep(this._yjsObserverHandler)
  }

  /**
   * observe 콜백 안에서 event를 plain object snapshot으로 변환.
   * 이후 setTimeout 에서 안전하게 사용 가능.
   */
  _snapshotEvent(event) {
    if (event instanceof Y.YArrayEvent && event.target === this.yBlocks) {
      return this._snapshotArrayEvent(event)
    }
    if (event instanceof Y.YMapEvent) {
      return this._snapshotMapEvent(event)
    }
    return null
  }

  _snapshotArrayEvent(event) {
    // event.changes.delta를 즉시 읽어 plain array로 복사
    const delta = []
    for (const d of event.changes.delta) {
      if (d.retain) {
        delta.push({ retain: d.retain })
      }
      if (d.delete) {
        delta.push({ delete: d.delete })
      }
      if (d.insert) {
        // insert된 Y.Map들을 즉시 blockData로 변환
        const blocks = []
        for (const item of d.insert) {
          if (item instanceof Y.Map) {
            blocks.push(this._yMapToBlockData(item))
          }
        }
        if (blocks.length > 0) {
          delta.push({ insert: blocks })
        }
      }
    }
    return { type: 'array', delta }
  }


  _snapshotMapEvent(event) {
    const yMap = event.target
    let blockId = yMap.get?.('id')
    let blockYMap = yMap

    // event.target이 nested data Y.Map이면 parent block Y.Map 찾기
    if (!blockId) {
      const found = this._findBlockForNestedYMap(yMap)
      if (!found) return null
      blockId = found.blockId
      blockYMap = found.blockYMap
    }

    // 모든 블록 타입의 data 변경을 동기화
    // (TEXT_BLOCK_TYPES의 text 필드는 YTextEvent에서 처리되므로 _applyMapSnapshot에서 필터)
    const blockData = this._yMapToBlockData(blockYMap)
    return { type: 'map', blockId, blockData }
  }

  // ──────────────────────────────────────
  // Snapshot queue → Editor 적용
  // ──────────────────────────────────────

  async _flushSnapshotQueue() {
    if (this._snapshotQueue.length === 0) return
    const snapshots = this._dedupeSnapshots(this._snapshotQueue.splice(0))

    const collabApi = this.editor.collab
    const applyAll = async () => {
      for (const snap of snapshots) {
        await this._applySnapshot(snap)
      }
    }

    this.isApplyingRemote = true
    this._lastRemoteApplyAt = Date.now()
    try {
      if (collabApi?.withRemoteApply) {
        await collabApi.withRemoteApply(applyAll)
      } else {
        await applyAll()
      }
    } catch (e) {
      console.warn('[YEditorBinding] remote apply error:', e)
    } finally {
      // grace period — 원격 DOM 변경 후 MutationObserver 연쇄 sync 방지
      // + editor.save() 직렬화 차이로 인한 무한 sync 루프 방지
      setTimeout(() => {
        this.isApplyingRemote = false
        this.awarenessCursor?.refreshRemoteCarets()
      }, 500)
    }
  }

  async _applySnapshot(snap) {
    switch (snap.type) {
      case 'array':
        await this._applyArraySnapshot(snap.delta)
        break
      case 'map':
        await this._applyMapSnapshot(snap.blockId, snap.blockData)
        break
    }
  }

  async _applyArraySnapshot(delta) {
    let index = 0

    for (const d of delta) {
      if (d.retain) {
        index += d.retain
      }
      if (d.delete) {
        for (let i = 0; i < d.delete; i++) {
          try {
            this.editor.blocks.delete(index)
          } catch (e) {
            console.warn('[YEditorBinding] block delete error at', index, e)
          }
        }
      }
      if (d.insert) {
        for (const blockData of d.insert) {
          try {
            this.editor.blocks.insert(
              blockData.type,
              blockData.data,
              undefined,
              index,
              false,
              false,
              blockData.id
            )
            // tunes 적용 (blocks.insert에는 tunes 파라미터가 없으므로 update로 후적용)
            if (blockData.tunes && Object.keys(blockData.tunes).length > 0) {
              await this.editor.blocks.update(blockData.id, undefined, blockData.tunes)
            }
          } catch (e) {
            console.warn('[YEditorBinding] block insert error at', index, e)
          }
          index++
        }
      }
    }
  }



  async _applyMapSnapshot(blockId, blockData) {
    const blockApi = this.editor.blocks.getById(blockId)
    if (!blockApi) return

    const updateData = { ...blockData.data }

    let patched = false

    // columns 블록: 전용 패치 (wrapper 설정 + nested content)
    if (blockData.type === 'columns') {
      if (await this._patchColumnsContent(blockId, blockData)) patched = true
    }

    // 모든 블록: DOM 직접 패치 시도 (깜빡임 방지)
    if (!patched && this._tryPatchBlock(blockApi.holder, blockData)) patched = true

    if (patched) {
      // data는 위 경로에서 DOM에 반영됨. tunes(block-style 등)는 별도 적용 필요.
      this._applyRemoteTunes(blockApi, blockData.tunes)
      return
    }

    // 패치 불가(구조 변경 등) → blocks.update fallback: data + tunes 모두 재적용
    try {
      await this.editor.blocks.update(blockId, updateData, blockData.tunes || undefined)
    } catch (e) {
      console.warn('[YEditorBinding] block update error for', blockId, e)
    }
  }

  /**
   * DOM 직접 패치 경로에서는 blocks.update이 호출되지 않으므로 tune 인스턴스에 수동 반영.
   * Tune wrapper는 wrap() 시 `data-tune-name="<등록명>"` 마커와 `_tuneInstance` 참조를 남긴다.
   * 등록명은 editor_core.js의 tools 설정 키와 일치 (camelCase: blockStyle, textStyle 등).
   * 현재 블록의 "직접" 자손 중 data-tune-name을 가진 요소만 대상으로 한다 (nested block 침범 방지).
   */
  _applyRemoteTunes(blockApi, incomingTunes) {
    const holder = blockApi?.holder
    if (!holder) return

    // 중첩 블록(columns 내부)의 tune wrapper는 제외. 이 블록의 content 경계 안쪽에서만 검색.
    // 안전 장치: 먼저 모든 tune wrapper를 모으되, 다른 블록(.ce-block) 조상을 가진 건 스킵.
    const allWrappers = Array.from(holder.querySelectorAll('[data-tune-name]'))
    const tuneWrappers = allWrappers.filter(w => w.closest('.ce-block') === holder)

    for (const wrapper of tuneWrappers) {
      const name = wrapper.dataset.tuneName
      const tune = wrapper._tuneInstance
      if (!tune || typeof tune.applyRemoteData !== 'function') continue
      const remoteData = incomingTunes?.[name] ?? null
      try {
        tune.applyRemoteData(remoteData)
      } catch (e) {
        console.warn('[YEditorBinding] tune.applyRemoteData error', name, e)
      }
    }
  }

  /**
   * Columns 블록의 nested block content를 DOM 직접 패치.
   * blocks.update()를 우회하여 깜빡임 없이 동기화.
   *
   * 패치 가능 조건: cols 수, 각 column의 block 수/ID가 모두 일치할 때만.
   * 구조 변경(Enter로 block 추가, Backspace로 삭제 등)은 false → fallback.
   * (nested editor의 DOM을 직접 조작하면 EditorJS 내부 상태와 불일치 발생)
   */
  async _patchColumnsContent(blockId, blockData) {
    const blockApi = this.editor.blocks.getById(blockId)
    if (!blockApi) return false

    const incomingCols = blockData.data?.cols
    if (!incomingCols) return false

    const columnsWrapper = blockApi.holder?.querySelector('.ce-columns')
    if (!columnsWrapper) return false
    const currentColEls = columnsWrapper.querySelectorAll(':scope > [class*="ce-columns__col"]')
    // cols 개수 변경은 nested editor 재생성이 불가피 → blocks.update fallback로 위임
    if (currentColEls.length !== incomingCols.length) return false

    // wrapper 설정(gap/ratios/responsive)을 tool 인스턴스에 반영
    // (render()에서 colWrapper._columnsTool = this 로 참조 노출됨)
    const tool = columnsWrapper._columnsTool
    if (tool && typeof tool.applyRemoteSettings === 'function') {
      tool.applyRemoteSettings(blockData.data)
    }

    for (let colIdx = 0; colIdx < incomingCols.length; colIdx++) {
      const incomingBlocks = incomingCols[colIdx]?.blocks || []
      const colHolder = document.getElementById(`${blockId}-${colIdx}`)
      if (!colHolder) return false

      const redactor = colHolder.querySelector('.codex-editor__redactor')
      if (!redactor) return false
      const currentNestedEls = Array.from(redactor.querySelectorAll(':scope > .ce-block[data-id]'))

      const idsMatch = currentNestedEls.length === incomingBlocks.length &&
        incomingBlocks.every((b, i) => !b.id || b.id === currentNestedEls[i]?.dataset.id)

      if (idsMatch) {
        // 구조 동일 → 각 block을 DOM 직접 패치
        for (let bIdx = 0; bIdx < incomingBlocks.length; bIdx++) {
          this._tryPatchBlock(currentNestedEls[bIdx], incomingBlocks[bIdx])
        }
      } else {
        // block 수/ID 변경 → nested editor의 blocks.render()
        const nestedEditor = this.nestedEditorRegistry.get(`${blockId}-${colIdx}`)
        if (nestedEditor) {
          try {
            await nestedEditor.blocks.render({ blocks: incomingBlocks })
          } catch {
            return false
          }
        } else {
          return false
        }
      }
    }
    return true
  }

  /**
   * 블록 DOM을 직접 패치 — blocks.update() 없이 contenteditable만 교체.
   * paragraph/header/quote, list, table 등 contenteditable 블록을 지원.
   * 패치 불가(구조 변경 등)하면 false 반환.
   */
  _tryPatchBlock(blockEl, blockData) {
    if (!blockEl || !blockData?.data) return false

    const type = blockData.type
    const data = blockData.data

    // 1. paragraph / header — 단일 text 필드
    if ((type === 'paragraph' || type === 'header') && typeof data.text === 'string') {
      const contentEl = blockEl.querySelector('[contenteditable="true"]')
      if (contentEl) {
        this._patchInnerHtml(contentEl, data.text)
        return true
      }
    }

    // 2. quote — text + caption (2개의 contenteditable)
    if (type === 'quote') {
      const textEl = blockEl.querySelector('.cdx-quote__text, [contenteditable="true"]')
      const captionEl = blockEl.querySelector('.cdx-quote__caption')
      let patched = false
      if (textEl && typeof data.text === 'string') {
        this._patchInnerHtml(textEl, data.text)
        patched = true
      }
      if (captionEl && typeof data.caption === 'string') {
        this._patchInnerHtml(captionEl, data.caption)
        // caption 내용 유무로 가시성 결정 — showCaption 토글 상태는 동기화되지 않으므로
        // remote 수신 측은 caption 데이터로 display를 복원해야 한다
        captionEl.style.display = data.caption.trim().length > 0 ? '' : 'none'
        patched = true
      }
      return patched
    }

    // 2.5 vote — question + options[].text (N개 contenteditable)
    // 옵션 수가 동일할 때만 DOM 패치 (텍스트 갱신). 옵션 수 변경은 false 반환해 blocks.update() fallback으로.
    if (type === 'vote' && typeof data.question === 'string' && Array.isArray(data.options)) {
      const questionEl = blockEl.querySelector('.vote-tool__question-input')
      const optionEls = blockEl.querySelectorAll('.vote-tool__option-input')
      if (!questionEl || optionEls.length !== data.options.length) return false

      this._patchInnerHtml(questionEl, data.question)
      for (let i = 0; i < data.options.length; i++) {
        const incoming = data.options[i]?.text ?? ''
        if (optionEls[i].textContent !== incoming) {
          this._patchInnerHtml(optionEls[i], incoming)
        }
      }
      return true
    }

    // 3. list — items[].content (재귀적 nested items)
    if (type === 'list' && Array.isArray(data.items)) {
      const flatTexts = []
      this._flattenListItems(data.items, flatTexts)
      const contentEls = blockEl.querySelectorAll('.cdx-nested-list__item-content, .cdx-list__item-content')
      // fallback: 전용 클래스가 없으면 contenteditable로
      const els = contentEls.length > 0 ? contentEls : blockEl.querySelectorAll('[contenteditable="true"]')
      if (els.length === flatTexts.length) {
        for (let i = 0; i < flatTexts.length; i++) {
          this._patchInnerHtml(els[i], flatTexts[i])
        }
        return true
      }
      return false
    }

    // 4. table — content[][] (2D 배열)
    if (type === 'table' && Array.isArray(data.content)) {
      const flatCells = data.content.flat()
      const cellEls = blockEl.querySelectorAll('td[contenteditable="true"], th[contenteditable="true"]')
      if (cellEls.length === flatCells.length) {
        for (let i = 0; i < flatCells.length; i++) {
          this._patchInnerHtml(cellEls[i], flatCells[i] || '')
        }
        return true
      }
      return false
    }

    // 5. raw / markdown — textarea 직접 패치
    if (type === 'raw' || type === 'markdown') {
      const textarea = blockEl.querySelector('textarea')
      if (textarea) {
        const incoming = data.html ?? data.markdown ?? ''
        if (textarea.value !== incoming) {
          const start = textarea.selectionStart
          const end = textarea.selectionEnd
          const hadFocus = document.activeElement === textarea
          textarea.value = incoming
          if (hadFocus) {
            textarea.setSelectionRange(
              Math.min(start, incoming.length),
              Math.min(end, incoming.length)
            )
          }
        }
        return true
      }
    }

    // ShikiCodeTool은 blocks.update() fallback (Shiki 하이라이팅 + 내부 상태 정합성)

    return false
  }

  /**
   * list items를 재귀적으로 평탄화하여 content 문자열 배열로 변환
   */
  _flattenListItems(items, result) {
    for (const item of items) {
      result.push(item.content || '')
      if (item.items && item.items.length > 0) {
        this._flattenListItems(item.items, result)
      }
    }
  }

  /**
   * 커서 위치를 보존하며 innerHTML 패치 (공용).
   *
   * data-empty 동기화: EditorJS 코어는 [data-placeholder][data-empty="true"]::before로
   * placeholder를 표시한다. data-empty는 브라우저의 'input' 이벤트 핸들러에서만 갱신되므로,
   * programmatic innerHTML 변경(= 여기)에서는 stale 상태("true")로 남아 placeholder가
   * 텍스트와 함께 보이는 버그가 난다. 우리가 직접 갱신한다.
   */
  _patchInnerHtml(contentEl, html) {
    if (contentEl.innerHTML === html) {
      this._syncEmptyMark(contentEl)
      return
    }

    const selection = window.getSelection()
    const hadFocus = contentEl.contains(selection?.anchorNode)
    let caretOffset = 0

    if (hadFocus && selection.rangeCount > 0) {
      caretOffset = getTextOffset(contentEl, selection.anchorNode, selection.anchorOffset)
    }

    contentEl.innerHTML = html

    this._syncEmptyMark(contentEl)

    if (hadFocus) {
      this._restoreCaretByOffset(contentEl, caretOffset)
    }
  }

  /**
   * EditorJS 코어가 사용하는 data-empty 속성을 현재 내용 기반으로 갱신.
   * data-placeholder / data-placeholder-active 둘 다 같은 attribute를 참조한다.
   */
  _syncEmptyMark(el) {
    if (!el || el.nodeType !== 1) return
    const text = (el.textContent || '').replace(/\u200B/g, '').trim()
    const hasContent = text.length > 0 || !!el.querySelector('img, iframe, video, audio')
    el.dataset.empty = hasContent ? 'false' : 'true'
  }

  // ──────────────────────────────────────
  // Editor → Yjs (Local changes)
  // ──────────────────────────────────────

  _setupEditorObserver() {
    // MutationObserver는 항상 필요 — nested editor (columns 내부) 변경은
    // fork의 onBlockChange로 감지되지 않으므로 DOM 감시가 필수
    this._setupMutationObserver()

    // onBlockChange도 추가 구독 — main editor의 block-level 변경을 즉시 감지
    const collabApi = this.editor.collab
    if (collabApi?.onBlockChange) {
      this._unsubscribeBlockChange = collabApi.onBlockChange(() => {
        if (this.isApplyingRemote || this.destroyed) return
        this._scheduleLocalSync()
      })
    }

    this._editorChangeCleanup = () => {
      this._unsubscribeBlockChange?.()
      this._mutationObserver?.disconnect()
      if (this._localSyncTimer) clearTimeout(this._localSyncTimer)
      if (this._mutationDebounceTimer) clearTimeout(this._mutationDebounceTimer)
    }
  }

  _scheduleLocalSync() {
    if (this._localSyncTimer) clearTimeout(this._localSyncTimer)
    this._localSyncTimer = setTimeout(() => {
      this._syncLocalToYjs()
    }, 50)
  }

  _setupMutationObserver() {
    const holderEl = document.getElementById(this.editor.configuration?.holder)
    if (!holderEl) return

    const redactor = holderEl.querySelector('.codex-editor__redactor')
    if (!redactor) return

    this._mutationDebounceTimer = null
    this._mutationObserver = new MutationObserver((mutations) => {
      if (this.isApplyingRemote || this.destroyed) return

      // popover/settings/toolbar UI 변경은 무시 — 설정 UI가 sync를 트리거하지 않도록
      const isUiOnly = mutations.every(m => {
        const el = m.target?.nodeType === Node.ELEMENT_NODE ? m.target : m.target?.parentElement
        return el?.closest?.('.ce-popover, .ce-toolbar, .ce-settings, [data-mutation-free]')
      })
      if (isUiOnly) return

      if (this._mutationDebounceTimer) clearTimeout(this._mutationDebounceTimer)
      this._mutationDebounceTimer = setTimeout(() => {
        this._scheduleLocalSync()
      }, 50)
    })

    this._mutationObserver.observe(redactor, {
      childList: true, subtree: true, characterData: true, attributes: false,
    })
  }

  async _syncLocalToYjs() {
    if (this.isApplyingRemote || this.isApplyingLocal) return
    if (this.destroyed) return

    // 원격 적용 직후 1초 이내에는 local sync 스킵
    // editor.save() 직렬화 차이로 인한 무한 sync 루프 방지
    if (Date.now() - this._lastRemoteApplyAt < 1000) return

    this.isApplyingLocal = true
    try {
      const outputData = await this.editor.save()
      if (!outputData?.blocks) return

      this.doc.transact(() => {
        this._diffAndApply(outputData.blocks)
      })
    } catch (e) {
      console.warn('[YEditorBinding] local sync error:', e)
    } finally {
      this.isApplyingLocal = false
    }
  }

  _diffAndApply(editorBlocks) {
    const editorIds = editorBlocks.map(b => b.id)

    const yIds = []
    for (let i = 0; i < this.yBlocks.length; i++) {
      const yMap = this.yBlocks.get(i)
      yIds.push(yMap instanceof Y.Map ? yMap.get('id') : yMap?.id || null)
    }

    // 삭제 (역순)
    for (let i = yIds.length - 1; i >= 0; i--) {
      if (!editorIds.includes(yIds[i])) {
        this.yBlocks.delete(i, 1)
      }
    }

    // 추가/업데이트
    for (let i = 0; i < editorBlocks.length; i++) {
      const block = editorBlocks[i]
      const yMap = this._findYMapById(block.id)

      if (!yMap) {
        const newMap = this._blockToYMap(block)
        this.yBlocks.insert(i, [newMap])
      } else if (yMap instanceof Y.Map) {
        this._updateYMapFromBlock(yMap, block)
      } else {
        // 서버 로드 plain object → Y.Map으로 교체
        const idx = this._findIndexById(block.id)
        if (idx !== -1) {
          this.yBlocks.delete(idx, 1)
          const newMap = this._blockToYMap(block)
          this.yBlocks.insert(idx, [newMap])
        }
      }
    }
  }

  _updateYMapFromBlock(yMap, block) {
    const data = block.data || {}
    const bigIntReplacer = (_, v) => typeof v === 'bigint' ? Number(v) : v

    const yData = yMap.get('data')
    if (yData instanceof Y.Map) {
      for (const [key, value] of Object.entries(data)) {
        const current = yData.get(key)
        // stringify 비교 + deep clone 저장 — yData.set()이 참조를 저장하므로
        // block tool이 내부 data를 in-place 수정하면 current === value가 되어 변경 감지 실패
        const valueStr = JSON.stringify(value, bigIntReplacer)
        if (JSON.stringify(current, bigIntReplacer) !== valueStr) {
          yData.set(key, JSON.parse(valueStr))
        }
      }
    }

    // tunes 동기화 — block-style 등 tune 변경이 원격에 전파되려면 Y.Map 전체 치환이 필요.
    // (_blockToYMap은 insert 시에만 tunes를 넣으므로 기존 블록 수정은 여기서 처리)
    const newTunes = block.tunes || null
    const currentTunes = yMap.get('tunes') ?? null
    const newStr = JSON.stringify(newTunes, bigIntReplacer)
    const curStr = JSON.stringify(currentTunes, bigIntReplacer)
    if (newStr !== curStr) {
      if (newTunes && Object.keys(newTunes).length > 0) {
        yMap.set('tunes', JSON.parse(newStr))
      } else if (currentTunes !== null) {
        yMap.delete('tunes')
      }
    }
  }

  // ──────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────

  _yBlocksToEditorData() {
    const blocks = []
    for (let i = 0; i < this.yBlocks.length; i++) {
      const item = this.yBlocks.get(i)
      if (item instanceof Y.Map) {
        blocks.push(this._yMapToBlockData(item))
      } else if (typeof item === 'object' && item !== null && item.type) {
        // 서버에서 로드한 데이터 (plain Hash → plain object, Y.Map 아님)
        // y-rb가 Ruby Integer를 BigInt로 변환하는 경우 대응
        blocks.push(safeClone(item))
      }
    }
    return blocks
  }

  _yMapToBlockData(yMap) {
    const id = yMap.get('id')
    const type = yMap.get('type')
    const data = {}
    const tunes = yMap.get('tunes')

    const yData = yMap.get('data')
    if (yData instanceof Y.Map) {
      for (const [key, value] of yData.entries()) {
        // deep clone — Y.Map 내부 참조를 block tool에 넘기면
        // tool이 in-place 수정 시 Y.Map 값까지 같이 바뀌어 diff가 깨짐
        data[key] = (typeof value === 'object' && value !== null)
          ? safeClone(value)
          : value
      }
    } else if (typeof yData === 'object' && yData !== null) {
      Object.assign(data, yData)
    }

    const result = { id, type, data }
    if (tunes) {
      result.tunes = (typeof tunes === 'object' && tunes !== null)
        ? safeClone(tunes)
        : tunes
    }
    return result
  }

  _blockToYMap(block) {
    const yMap = new Y.Map()
    yMap.set('id', block.id || crypto.randomUUID?.() || Math.random().toString(36).slice(2, 12))
    yMap.set('type', block.type)

    const yData = new Y.Map()
    const data = block.data || {}

    for (const [key, value] of Object.entries(data)) {
      // deep clone — block tool 내부 참조와 Y.Map 참조를 분리
      yData.set(key, (typeof value === 'object' && value !== null)
        ? safeClone(value)
        : value)
    }

    yMap.set('data', yData)
    if (block.tunes) {
      yMap.set('tunes', safeClone(block.tunes))
    }
    return yMap
  }

  /**
   * nested data Y.Map → parent block Y.Map 찾기
   * (observeDeep에서 event.target이 data Y.Map일 때 block id를 역추적)
   */
  _findBlockForNestedYMap(yMap) {
    for (let i = 0; i < this.yBlocks.length; i++) {
      const blockYMap = this.yBlocks.get(i)
      if (!blockYMap) continue
      const data = blockYMap instanceof Y.Map ? blockYMap.get('data') : blockYMap?.data
      if (data === yMap) {
        const blockId = blockYMap instanceof Y.Map ? blockYMap.get('id') : blockYMap?.id
        return { blockId, blockYMap }
      }
    }
    return null
  }

  /**
   * 같은 blockId의 map snapshot 중복 제거 (마지막 것만 유지)
   */
  _dedupeSnapshots(snapshots) {
    const lastMapByBlockId = new Map()
    const result = []
    for (const snap of snapshots) {
      if (snap.type === 'map') {
        lastMapByBlockId.set(snap.blockId, snap)
      } else {
        result.push(snap)
      }
    }
    result.push(...lastMapByBlockId.values())
    return result
  }

  _findIndexById(blockId) {
    for (let i = 0; i < this.yBlocks.length; i++) {
      const item = this.yBlocks.get(i)
      const id = item instanceof Y.Map ? item.get('id') : item?.id
      if (id === blockId) return i
    }
    return -1
  }

  _findYMapById(blockId) {
    for (let i = 0; i < this.yBlocks.length; i++) {
      const item = this.yBlocks.get(i)
      const id = item instanceof Y.Map ? item.get('id') : item?.id
      if (id === blockId) return item
    }
    return null
  }


  /**
   * text offset으로 커서 복원
   */
  _restoreCaretByOffset(container, targetOffset) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    let offset = 0
    while (walker.nextNode()) {
      const node = walker.currentNode
      const len = node.textContent.length
      if (offset + len >= targetOffset) {
        try {
          const sel = window.getSelection()
          const range = document.createRange()
          range.setStart(node, Math.min(targetOffset - offset, len))
          range.collapse(true)
          sel.removeAllRanges()
          sel.addRange(range)
        } catch { /* 복원 실패 시 무시 */ }
        return
      }
      offset += len
    }
  }
}
